/**
* Responsibility:
* - Resolve page numbers and page elements for Gravity Forms multipage flows.
*
* Touches:
* - Reads page ids (`gform_page_*_*`) and page/class relationships in form DOM.
*
* Guarantees:
* - Returns normalized page collections and page numbers for configured selectors.
*/
function normalizeClassName(className) {
	return typeof className === 'string' ? className.trim() : '';
}

/**
* Responsibility:
* - Resolve 1-based GF page number from a `.gform_page` element id.
*
* Touches:
* - Reads `id` attribute and parses `gform_page_{formId}_{pageNumber}`.
*
* Guarantees:
* - Returns `0` when page id is missing/invalid.
*/
export function getPageNumber(page) {
	if (!(page instanceof Element)) {
		return 0;
	}

	const id = page.getAttribute('id') || '';
	const match = id.match(/gform_page_\d+_(\d+)/);
	if (!match) {
		return 0;
	}

	return Number.parseInt(match[1], 10) || 0;
}

/**
* Responsibility:
* - Resolve one rendered GF page element by its page-field marker id.
*
* Touches:
* - Queries `.gform_page[data-js="page-field-id-{fieldId}"]` within the current form.
*
* Guarantees:
* - Returns the matching page element or `null` when unavailable.
*/
export function findPageByFieldId(form, fieldId) {
	if (!(form instanceof HTMLElement)) {
		return null;
	}

	const normalizedFieldId = Number.parseInt(String(fieldId || ''), 10);
	if (!Number.isFinite(normalizedFieldId) || normalizedFieldId < 0) {
		return null;
	}

	const page = form.querySelector(`.gform_page[data-js="page-field-id-${normalizedFieldId}"]`);
	return page instanceof HTMLElement ? page : null;
}

/**
* Responsibility:
* - Find GF pages containing the configured class on page node or descendants.
*
* Touches:
* - Reads `.gform_page` elements and evaluates selector/class membership.
*
* Guarantees:
* - Returns an ordered array of matching page elements.
*/
export function findPagesByClass(form, className) {
	const normalizedClass = normalizeClassName(className);
	if (!(form instanceof HTMLElement) || normalizedClass === '') {
		return [];
	}

	const pages = Array.from(form.querySelectorAll('.gform_page'));
	return pages.filter((page) => {
		if (!(page instanceof HTMLElement)) {
			return false;
		}

		return page.classList.contains(normalizedClass) || page.querySelector(`.${normalizedClass}`) !== null;
	});
}

/**
* Responsibility:
* - Resolve unique page numbers for pages matching a configured class.
*
* Touches:
* - Uses `findPagesByClass()` and parses numbers with `getPageNumber()`.
*
* Guarantees:
* - Returns a sorted array of positive page numbers without duplicates.
*/
export function findPageNumbersByClass(form, className) {
	const numbers = findPagesByClass(form, className)
		.map((page) => getPageNumber(page))
		.filter((pageNumber) => pageNumber > 0);

	return Array.from(new Set(numbers)).sort((a, b) => a - b);
}

/**
* Responsibility:
* - Resolve the first page that matches a configured class selector.
*
* Touches:
* - Uses `findPagesByClass()` against form DOM.
*
* Guarantees:
* - Returns first matched page element or `null`.
*/
export function getFirstPageByClass(form, className) {
	const pages = findPagesByClass(form, className);
	return pages.length > 0 ? pages[0] : null;
}
